/**
 * SettlementStepPage – Generic wrapper that loads progress data from the API
 * and renders StepDetail for any settlement step.
 *
 * Each SFrame step screen simply passes in its step config and estate context.
 * This keeps step screens thin and consistent.
 */

import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StepDetail from './StepDetail';
import DocumentUploadSection from './DocumentUploadSection';
import PlannerDataBadge from './PlannerDataBadge';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { useSettlement } from '../../contexts/SettlementContext';
import type { SettlementStep } from '../../utils/settlementFlowConfig';
import type { NotificationStatus } from '../../utils/notificationTrackingTypes';
import { collectStepFormFieldIds } from '../../utils/settlementFlowConfig';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  toDocumentUploadRow,
  uploadSettlementDocument,
} from '../../utils/settlementDocuments';
import { SETTLEMENT_PLANNER_LAYOUT } from './settlementTheme';

export type StepStatus = 'not-started' | 'in-progress' | 'complete';

interface SettlementDocumentContext {
  phase: string;
  stepId: string;
}

interface SettlementStepPageProps {
  /** The step config from settlementFlowConfig */
  step: SettlementStep;
  /** The phase ID (e.g., 'iad') */
  phase: string;
  /** Route to navigate back to the phase overview */
  overviewRoute: string;
  /** Optional: planner frame IDs to pull read-only planner data from */
  plannerFrameIds?: string[];
  /** When true, suppress the generic green planner banner (use PlannerDataSection in children instead) */
  hidePlannerBanner?: boolean;
  /** Optional segmented status control (Not Started / In Progress / Complete) */
  statusControl?: 'none' | 'segmented';
  /** Optional content above StepDetail (e.g. in-step progress tracker) */
  topContent?: ReactNode;
  /** When set, document list/upload/delete uses settlement_documents API for this phase/step */
  settlementDocumentContext?: SettlementDocumentContext;
  /** Optional: custom content to render below the step detail */
  children?: React.ReactNode;
  /** Optional: custom appearance for the step page */
  layoutVariant?: 'default' | 'planner';
  /** When 'bottom', renders the Notes field after children instead of inside StepDetail */
  notesPosition?: 'default' | 'bottom';
  /** When provided, frame controls the displayed status (overrides internal state for display) */
  externalStatus?: StepStatus;
  /** Called when user manually clicks a status button and externalStatus is provided; frame handles persistence */
  onStatusChange?: (status: StepStatus) => void;
  /** Called when notes field value changes */
  onNotesUpdate?: (notes: string) => void;
  /** Called when the misc/bottom document list count changes */
  onMiscDocCountChange?: (count: number) => void;
  /** When true, suppresses the generic Notes textarea */
  hideNotes?: boolean;
  /** When true, suppresses the generic bottom DocumentUploadSection */
  hideDocuments?: boolean;
}

export default function SettlementStepPage({
  step,
  phase,
  overviewRoute,
  plannerFrameIds,
  hidePlannerBanner,
  statusControl = 'none',
  topContent,
  settlementDocumentContext,
  children,
  layoutVariant = 'planner',
  notesPosition = 'default',
  externalStatus,
  onStatusChange,
  onNotesUpdate,
  onMiscDocCountChange,
  hideNotes = false,
  hideDocuments = false,
}: SettlementStepPageProps) {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const estateId = paramEstateId || null;
  const { isArchived } = useSettlement();
  const isPlanner = layoutVariant === 'planner';
  const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
  const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
  const childrenColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.childrenColumn : 'max-w-3xl mx-auto mb-6';
  const {
    isLoading,
    error,
    loadPhaseProgress,
    updateStepProgress,
    toggleChecklistItem,
    toggleSubTask,
    loadPlannerData,
    loadFrameData,
    saveFrameData,
  } = useSettlementSession(estateId, { readOnly: isArchived });

  const [status, setStatus] = useState<StepStatus>('not-started');
  const [completedSubTaskIds, setCompletedSubTaskIds] = useState<Set<string>>(new Set());
  const [completedChecklistItemIds, setCompletedChecklistItemIds] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<Array<{ id: string; name: string; type: string; size: number; uploadedAt: string; uploadedBy?: string }>>([]);
  const [plannerData, setPlannerData] = useState<Record<string, unknown> | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const formFrameId = `${phase}-step-forms-${step.id}`;
  const formLoadedRef = useRef(false);

  const [trackedCounts, setTrackedCounts] = useState<Record<string, number>>({});
  const [trackerModal, setTrackerModal] = useState<{ itemId: string; suggestedName?: string; suggestedType?: string } | null>(null);
  const [trackerForm, setTrackerForm] = useState({ type: '', name: '', status: 'pending' as const, deadline: '', notes: '' });

  const formFieldIds = useMemo(() => collectStepFormFieldIds(step), [step]);
  const hasStructuredForm = formFieldIds.length > 0;

  // Load step progress on mount
  useEffect(() => {
    if (!estateId) return;

    const load = async () => {
      const steps = await loadPhaseProgress(phase);
      const thisStep = steps.find((s) => s.stepId === step.id);
      if (thisStep) {
        setStatus(thisStep.status as StepStatus);
        setCompletedSubTaskIds(new Set(thisStep.completedSubTasks || []));
        setCompletedChecklistItemIds(new Set(thisStep.completedChecklistItems || []));
        setNotes(thisStep.notes || '');
        if (!settlementDocumentContext) {
          setDocuments(thisStep.documents || []);
        }
      }
    };
    load();
  }, [estateId, phase, step.id, loadPhaseProgress, settlementDocumentContext]);

  // Load structured form answers (shared frame data for this step)
  useEffect(() => {
    formLoadedRef.current = false;
    const defaults: Record<string, string> = {};
    for (const id of formFieldIds) defaults[id] = '';
    if (!estateId) {
      setFormValues(defaults);
      return;
    }
    let cancelled = false;
    (async () => {
      const fd = await loadFrameData(formFrameId);
      const saved = (fd?.data || {}) as Record<string, unknown>;
      const merged = { ...defaults };
      for (const k of Object.keys(defaults)) {
        if (saved[k] != null && saved[k] !== '') merged[k] = String(saved[k]);
      }
      if (!cancelled) {
        setFormValues(merged);
        formLoadedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, formFrameId, loadFrameData, formFieldIds]);

  // Persist form field changes (debounced)
  useEffect(() => {
    if (!estateId || isArchived || !formLoadedRef.current || !hasStructuredForm) return;
    const t = setTimeout(() => {
      void saveFrameData(formFrameId, formValues);
    }, 700);
    return () => clearTimeout(t);
  }, [formValues, estateId, isArchived, formFrameId, saveFrameData, hasStructuredForm]);

  // Load documents from settlement_documents API when configured (overrides progress JSON)
  useEffect(() => {
    if (!estateId || !settlementDocumentContext) return;

    const loadDocs = async () => {
      try {
        const rows = await listSettlementDocuments(
          estateId,
          settlementDocumentContext.phase,
          settlementDocumentContext.stepId
        );
        setDocuments(rows.map(toDocumentUploadRow));
      } catch (e) {
        console.error('[SettlementStepPage] Failed to load settlement documents', e);
      }
    };
    loadDocs();
  }, [estateId, settlementDocumentContext]);

  // Load planner data if applicable
  useEffect(() => {
    if (!estateId || !plannerFrameIds || plannerFrameIds.length === 0) return;

    const load = async () => {
      for (const frameId of plannerFrameIds) {
        const data = await loadPlannerData(frameId);
        if (data) {
          setPlannerData(data);
          break; // Use first non-null result
        }
      }
    };
    load();
  }, [estateId, plannerFrameIds, loadPlannerData]);

  // Mark step as in-progress on first interaction
  const ensureInProgress = useCallback(async () => {
    if (status === 'not-started' && estateId) {
      setStatus('in-progress');
      await updateStepProgress(step.id, phase, { status: 'in-progress' });
    }
  }, [status, estateId, step.id, phase, updateStepProgress]);

  const handleToggleChecklistItem = useCallback(
    async (itemId: string, checked: boolean) => {
      await ensureInProgress();

      setCompletedChecklistItemIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(itemId);
        else next.delete(itemId);
        return next;
      });

      if (estateId) {
        await toggleChecklistItem(step.id, phase, itemId, checked);
      }
    },
    [ensureInProgress, estateId, step.id, phase, toggleChecklistItem]
  );

  const handleToggleSubTask = useCallback(
    async (subTaskId: string, checked: boolean) => {
      await ensureInProgress();

      setCompletedSubTaskIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(subTaskId);
        else next.delete(subTaskId);
        return next;
      });

      if (estateId) {
        await toggleSubTask(step.id, phase, subTaskId, checked);
      }
    },
    [ensureInProgress, estateId, step.id, phase, toggleSubTask]
  );

  const handleNotesChange = useCallback(
    async (newNotes: string) => {
      setNotes(newNotes);
      if (estateId) {
        await updateStepProgress(step.id, phase, { notes: newNotes });
      }
      onNotesUpdate?.(newNotes);
    },
    [estateId, step.id, phase, updateStepProgress, onNotesUpdate]
  );

  const handleDocUpload = useCallback(
    async (file: File): Promise<void> => {
      if (!estateId || isArchived) return;
      if (settlementDocumentContext) {
        const uploaded = await uploadSettlementDocument({
          estateId,
          phase: settlementDocumentContext.phase,
          stepId: settlementDocumentContext.stepId,
          file,
        });
        setDocuments((prev) => {
          const next = [...prev, toDocumentUploadRow(uploaded)];
          onMiscDocCountChange?.(next.length);
          return next;
        });
        return;
      }
      const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const doc = {
        id,
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        uploadedAt: new Date().toISOString(),
        uploadedBy: estateId,
      };
      setDocuments((prev) => {
        const next = [...prev, doc as { id: string; name: string; type: string; size: number; uploadedAt: string; uploadedBy: string }];
        const persisted = next.map((item) => ({
          ...item,
          uploadedBy: item.uploadedBy || estateId || '',
        }));
        void updateStepProgress(step.id, phase, { documents: persisted });
        onMiscDocCountChange?.(next.length);
        return next;
      });
    },
    [estateId, isArchived, settlementDocumentContext, updateStepProgress, step.id, phase, onMiscDocCountChange]
  );

  const handleDocDelete = useCallback(
    async (docId: string): Promise<void> => {
      if (!estateId || isArchived) return;
      if (settlementDocumentContext) {
        await deleteSettlementDocument(estateId, docId);
        setDocuments((prev) => {
          const next = prev.filter((d) => d.id !== docId);
          onMiscDocCountChange?.(next.length);
          return next;
        });
        return;
      }
      setDocuments((prev) => {
        const next = prev.filter((d) => d.id !== docId);
        const persisted = next.map((item) => ({
          ...item,
          uploadedBy: item.uploadedBy || estateId || '',
        }));
        void updateStepProgress(step.id, phase, { documents: persisted });
        onMiscDocCountChange?.(next.length);
        return next;
      });
    },
    [estateId, isArchived, settlementDocumentContext, updateStepProgress, step.id, phase, onMiscDocCountChange]
  );

  const handleFormChange = useCallback((fieldId: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleExplicitStatus = useCallback(
    async (next: StepStatus) => {
      if (isArchived) return;
      if (externalStatus !== undefined && onStatusChange) {
        onStatusChange(next);
        return;
      }
      setStatus(next);
      if (estateId) {
        await updateStepProgress(step.id, phase, { status: next });
      }
    },
    [isArchived, externalStatus, onStatusChange, estateId, step.id, phase, updateStepProgress]
  );

  const loadGlobalTrackerCounts = useCallback(async () => {
    if (!estateId) return;
    try {
      const fd = await loadFrameData('sframe-180');
      const base = fd?.data && typeof fd.data === 'object' ? (fd.data as Record<string, unknown>) : {};
      const notifData = base.notificationTracking as Record<string, unknown> | undefined;
      const list = Array.isArray(notifData?.institutions)
        ? (notifData!.institutions as Array<Record<string, unknown>>)
        : [];
      const counts: Record<string, number> = {};
      for (const row of list) {
        const plannerRef = row.plannerRef ? String(row.plannerRef) : '';
        if (plannerRef.startsWith(`checklist:${step.id}:`)) {
          const itemId = plannerRef.split(':')[2];
          counts[itemId] = (counts[itemId] || 0) + 1;
        }
      }
      setTrackedCounts(counts);
    } catch (e) {
      console.error('[SettlementStepPage] Failed to load tracker counts', e);
    }
  }, [estateId, loadFrameData, step.id]);

  useEffect(() => {
    void loadGlobalTrackerCounts();
  }, [loadGlobalTrackerCounts]);

  // Listen for tracker updates from overview page
  useEffect(() => {
    const handleTrackerUpdate = () => {
      void loadGlobalTrackerCounts();
    };
    window.addEventListener('notificationTracking:updated', handleTrackerUpdate);
    return () => window.removeEventListener('notificationTracking:updated', handleTrackerUpdate);
  }, [loadGlobalTrackerCounts]);

  const openAddToTracker = useCallback((itemId: string, suggestedName?: string, suggestedType?: string) => {
    setTrackerForm({ type: suggestedType || '', name: suggestedName || '', status: 'pending', deadline: '', notes: '' });
    setTrackerModal({ itemId, suggestedName, suggestedType });
  }, []);

  const closeTracker = useCallback(() => setTrackerModal(null), []);

  const saveTrackerEntry = useCallback(async () => {
    if (!estateId || !trackerModal) return;
    const plannerRef = `checklist:${step.id}:${trackerModal.itemId}`;
    try {
      const fd = await loadFrameData('sframe-180');
      const base = fd?.data && typeof fd.data === 'object' ? (fd.data as Record<string, unknown>) : {};
      const notifTracking = (base.notificationTracking as Record<string, unknown>) || {};
      const institutions = Array.isArray(notifTracking.institutions) ? (notifTracking.institutions as Record<string, unknown>[]) : [];
      const nextRow = {
        id: crypto.randomUUID(),
        name: trackerForm.name || 'Unnamed',
        category: trackerForm.type || 'Custom',
        status: trackerForm.status,
        deadlineDate: trackerForm.deadline || undefined,
        reminderDaysBefore: 7,
        notes: trackerForm.notes || undefined,
        guidanceText: undefined,
        plannerRef,
      };
      const merged = { ...base, notificationTracking: { ...notifTracking, institutions: [...institutions, nextRow] } };
      await saveFrameData('sframe-180', merged);
      // Dispatch update event for any listening components
      window.dispatchEvent(new CustomEvent('notificationTracking:updated'));
      setTrackedCounts((prev) => ({ ...prev, [trackerModal.itemId]: (prev[trackerModal.itemId] || 0) + 1 }));
      setTrackerModal(null);
    } catch (e) {
      console.error('[SettlementStepPage] Failed to save tracker entry', e);
    }
  }, [estateId, trackerModal, trackerForm, loadFrameData, saveFrameData, step.id]);


  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {topContent && <div className={isPlanner ? 'mb-6' : 'max-w-3xl mx-auto mb-6'}>{topContent}</div>}

        {statusControl === 'segmented' && (
          <div className={isPlanner ? 'mb-6' : 'max-w-3xl mx-auto mb-6'}>
            <p className="text-xs font-semibold text-stone-600 mb-2 uppercase tracking-wide">Step status</p>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: 'not-started' as const, label: 'Not Started' },
                  { key: 'in-progress' as const, label: 'In Progress' },
                  { key: 'complete' as const, label: 'Complete' },
                ]
              ).map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  disabled={!estateId || isArchived}
                  onClick={() => handleExplicitStatus(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    (externalStatus ?? status) === key
                      ? key === 'complete'
                        ? 'bg-green-600 border-green-700 text-white'
                        : key === 'in-progress'
                          ? 'bg-amber-100 border-amber-400 text-amber-950'
                          : 'bg-cyan-600 border-cyan-700 text-white'
                      : 'bg-white border-stone-300 text-stone-700 hover:border-stone-400'
                  } ${!estateId || isArchived ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {!estateId && (
              <p className="text-xs text-stone-500 mt-2">Open this step from an estate workspace to save status.</p>
            )}
          </div>
        )}

        {/* Planner data banner */}
        {!hidePlannerBanner && plannerData && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <PlannerDataBadge variant="from-planning" />
              <p className="text-sm text-emerald-900">
                Some information for this step was provided during estate planning. Review the pre-populated data below.
              </p>
            </div>
          </div>
        )}

        <StepDetail
          step={step}
          completedSubTaskIds={completedSubTaskIds}
          completedChecklistItemIds={completedChecklistItemIds}
          onToggleChecklistItem={handleToggleChecklistItem}
          onToggleSubTask={handleToggleSubTask}
          onBack={() => navigate(overviewRoute)}
          notes={hideNotes || notesPosition === 'bottom' ? undefined : notes}
          onNotesChange={hideNotes || notesPosition === 'bottom' ? undefined : handleNotesChange}
          formValues={hasStructuredForm ? formValues : undefined}
          onFormChange={hasStructuredForm ? handleFormChange : undefined}
          disabled={isArchived}
          layoutVariant={layoutVariant}
          trackedCounts={trackedCounts}
          onAddToTracker={openAddToTracker}
        />

        {/* Step-specific forms and guidance */}
        {children && <div className={childrenColumnClassName}>{children}</div>}

        {/* Notes at bottom (when notesPosition="bottom") */}
        {!hideNotes && notesPosition === 'bottom' && (
          <div className={`${childrenColumnClassName} mb-8`}>
            <h2 className={SETTLEMENT_PLANNER_LAYOUT.notesLabel}>Notes</h2>
            <textarea
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              disabled={isArchived}
              placeholder="Add any notes about this step..."
              className={SETTLEMENT_PLANNER_LAYOUT.notesInput}
              rows={3}
            />
          </div>
        )}

        {/* Document upload */}
        {!hideDocuments && (
          <div className={childrenColumnClassName}>
            <DocumentUploadSection
              documents={documents}
              onUpload={handleDocUpload}
              onDelete={estateId && !isArchived ? handleDocDelete : undefined}
              disabled={!estateId || isArchived}
              className="mb-8"
            />
          </div>
        )}

        {isLoading && (
          <div className="text-center text-stone-500 text-sm py-4">Saving...</div>
        )}

        {/* Add to Tracker Modal */}
        {trackerModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <h2 className="text-lg font-bold text-black mb-4">Add to Tracker</h2>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Type</label>
                  <select
                    value={trackerForm.type}
                    onChange={(e) => setTrackerForm({ ...trackerForm, type: e.target.value })}
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                    aria-required
                  >
                    <option value="" disabled>
                      Select type
                    </option>
                    <option>Government Agency</option>
                    <option>Financial Institution</option>
                    <option>Employer / Business</option>
                    <option>Service / Subscription</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Institution / Party Name</label>
                  <input
                    type="text"
                    value={trackerForm.name}
                    onChange={(e) => setTrackerForm({ ...trackerForm, name: e.target.value })}
                    placeholder="Name"
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Status</label>
                  <select
                    value={trackerForm.status}
                    onChange={(e) => setTrackerForm({ ...trackerForm, status: e.target.value as NotificationStatus })}
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="pending">Not Started</option>
                    <option value="sent">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Deadline</label>
                  <input
                    type="date"
                    value={trackerForm.deadline}
                    onChange={(e) => setTrackerForm({ ...trackerForm, deadline: e.target.value })}
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Notes</label>
                  <textarea
                    value={trackerForm.notes}
                    onChange={(e) => setTrackerForm({ ...trackerForm, notes: e.target.value })}
                    placeholder=""
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm resize-none min-h-[70px]"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeTracker}
                  className="flex-1 rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveTrackerEntry}
                  disabled={!trackerForm.type || !trackerForm.name}
                  className={`flex-1 rounded px-4 py-2 text-sm font-semibold ${!trackerForm.type || !trackerForm.name ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-teal-700 text-white hover:bg-teal-800'}`}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
